import {
  MantineProvider,
  Group,
  AppShell,
  AppShellHeader,
  AppShellMain,
  Container,
  Stack,
  mergeMantineTheme,
  DEFAULT_THEME,
  Pagination,
} from "@mantine/core"
import { DesignStudioParametersToolkit } from "@tfg/ui"
import _ from "lodash"
import { SetStateAction, Dispatch, useCallback, useMemo } from "react"

import {
  CommunicationStudioClasses,
  CUSTOMISATION_CATEGORIES,
} from "@tfg/types/constants/communication-studio.constant"
import { searchEngineTextColorCSSVariable } from "@tfg/types/schemas/communication-studio/cssVariables"
import { StylingParameters } from "@tfg/types/types/communication-studio/styling-parameters.types"

import classes from "~/assets/css/SearchEngine.module.css"
import { DataFetchWrapper } from "~/components/DataFetchWrapper"
import { usePublicProductsQuery } from "~/pages/Public/hooks"

import { ProductsGrid } from "./ProductsGrid"
import { SearchEngineHeader } from "./SearchEngineHeader"

export const SearchEngine = ({
  stylingParameters,
  logoUrl,
  slug,
  setSearchParams,
  searchParams,
}: {
  stylingParameters: StylingParameters
  slug: string
  language: string
  logoUrl: string | null
  setSearchParams: Dispatch<SetStateAction<URLSearchParams>>
  searchParams: URLSearchParams
}) => {
  const productsQuery = usePublicProductsQuery(slug, searchParams)
  const changeFilter = useCallback(
    (key: string, value: string | null) => {
      setSearchParams((prev) => {
        if (value == null) {
          prev.delete(key)
        } else {
          prev.set(key, value)
        }
        return prev
      })
    },
    [setSearchParams]
  )

  const debouncedChangeFilter = useMemo(
    () => _.debounce(changeFilter, 300),
    [changeFilter]
  )

  const handleFilter = useCallback(
    (key: string, value: string | null) => {
      debouncedChangeFilter(key, value)
    },
    [debouncedChangeFilter]
  )

  const { fontFamily } =
    stylingParameters[CUSTOMISATION_CATEGORIES.SEARCH_ENGINE]

  DesignStudioParametersToolkit.injectCssVariablesToDocumentHead(
    stylingParameters
  )
  DesignStudioParametersToolkit.injectAllClassesToDocumentHead(
    stylingParameters
  )
  const themeOverride = {
    fontFamily,
    headings: {
      fontFamily,
    },
  }
  const theme = mergeMantineTheme(DEFAULT_THEME, themeOverride)

  return (
    <MantineProvider theme={theme}>
      <AppShell
        padding={0}
        layout="alt"
        classNames={classes}
        styles={{
          main: {
            height: "100vh",
            overflow: "auto",
          },
        }}
        header={{ height: { base: "18rem", xs: "16rem", sm: "12rem" } }}
      >
        <DataFetchWrapper query={productsQuery}>
          {({ data: products }) => {
            return (
              <>
                <AppShellHeader
                  className={CommunicationStudioClasses.SEARCH_ENGINE_HEADER}
                >
                  <SearchEngineHeader
                    logoUrl={logoUrl}
                    fontFamily={fontFamily ?? DEFAULT_THEME.fontFamily}
                    values={searchParams}
                    types={products.types ?? []}
                    collections={products.collections ?? []}
                    onFilter={handleFilter}
                    brands={products.brands ?? []}
                    searchEngineBrands={products.searchEngineBrands ?? []}
                  />
                </AppShellHeader>
                <AppShellMain
                  style={DesignStudioParametersToolkit.spreadStylingParameters({
                    category: CUSTOMISATION_CATEGORIES.SEARCH_ENGINE,
                    stylingParameters,
                    stylingParamKeys: ["backgroundColor"],
                  })}
                >
                  <Container my="xl" size="xl">
                    <Stack>
                      <Group justify="right">
                        <Pagination
                          color={DesignStudioParametersToolkit.getCssVariableLiteralName(
                            searchEngineTextColorCSSVariable
                          )}
                          value={products.page}
                          onChange={(page) => {
                            setSearchParams((prev) => {
                              prev.set("page", page.toString())
                              return prev
                            })
                          }}
                          total={products.totalPages}
                        />
                      </Group>
                      <ProductsGrid products={products.products} />
                      <Group justify="right">
                        <Pagination
                          color={DesignStudioParametersToolkit.getCssVariableLiteralName(
                            searchEngineTextColorCSSVariable
                          )}
                          value={products.page}
                          onChange={(page) => {
                            setSearchParams((prev) => {
                              prev.set("page", page.toString())
                              return prev
                            })
                          }}
                          total={products.totalPages}
                        />
                      </Group>
                    </Stack>
                  </Container>
                </AppShellMain>
              </>
            )
          }}
        </DataFetchWrapper>
      </AppShell>
    </MantineProvider>
  )
}
